<template>
  <!--
    Nút lọc cột kiểu Excel AutoFilter dùng chung cho bảng HTML thuần:
    icon lọc ở header → dropdown (ô tìm KHÔNG dấu + "Chọn tất cả" + danh sách giá trị click-chọn + Áp dụng/Xóa lọc).
    Mỗi item là ROW click-được + icon check (theo pattern BDropdownCustom) — b-form-checkbox array-v-model
    KHÔNG ăn click ổn định trong dropdown/theme này nên item không tick được.
    v-model = mảng giá trị (hiển thị) ĐANG chọn; rỗng = KHÔNG lọc (hiện tất cả). Cha tự lọc dữ liệu.
  -->
  <b-dropdown
    ref="dd"
    variant="link"
    size="sm"
    no-caret
    right
    lazy
    :toggle-class="toggleClass"
    :menu-class="[
      'review-col-filter-menu',
      { 'review-col-filter-menu--expanded': expanded },
    ]"
    @show="onOpen"
  >
    <template #button-content>
      <span
        class="review-col-filter-btn"
        :aria-label="isActive ? `Đang lọc ${label}` : `Lọc ${label}`"
        :title="isActive ? `Đang lọc: ${label}` : `Lọc: ${label}`"
      >
        <i
          aria-hidden="true"
          :class="isActive ? 'fas fa-filter' : 'fas fa-chevron-down'"
        ></i>
      </span>
    </template>

    <b-dropdown-form class="review-col-filter-form">
      <div class="review-col-filter-caption">{{ label }}</div>
      <div class="d-flex align-items-center mb-2">
        <b-form-input
          v-model="search"
          size="sm"
          class="flex-grow-1"
          placeholder="Tìm..."
          autocomplete="off"
        />
        <b-button
          variant="light"
          size="sm"
          class="ml-1 review-col-filter-expand"
          :title="expanded ? 'Thu nhỏ khung' : 'Mở rộng khung'"
          :aria-label="expanded ? 'Thu nhỏ khung' : 'Mở rộng khung'"
          @click="expanded = !expanded"
        >
          <i
            aria-hidden="true"
            :class="expanded ? 'fas fa-compress-alt' : 'fas fa-expand-alt'"
          ></i>
        </b-button>
      </div>

      <!-- Chọn tất cả (row click, có trạng thái tick-hết / tick-một-phần) -->
      <div
        class="review-col-filter-item review-col-filter-all font-weight-bold"
        @click="toggleSelectAllRow"
      >
        <i class="review-col-filter-check" :class="selectAllIconClass"></i>
        <span class="ml-1">Chọn tất cả</span>
      </div>

      <div
        ref="list"
        class="review-col-filter-list"
        :class="{
          'review-col-filter-list--expanded': expanded,
          'is-dragging': !!drag,
        }"
        @pointerdown="onItemPointerDown"
        @pointermove="onItemPointerMove"
        @pointerup="onItemPointerUp"
        @pointercancel="onItemPointerUp"
      >
        <div
          v-for="(opt, index) in visibleOptions"
          :key="opt.value"
          :data-index="index"
          class="review-col-filter-item"
          :class="{ 'is-selected': isChecked(opt.value) }"
          :title="opt.label"
          @click="onItemClick(opt.value)"
        >
          <i
            class="review-col-filter-check"
            :class="
              isChecked(opt.value)
                ? 'fas fa-check-square text-primary'
                : 'far fa-square text-muted'
            "
          ></i>
          <span class="ml-1 review-col-filter-label">{{ opt.label }}</span>
        </div>
        <div
          v-if="!visibleOptions.length"
          class="text-muted small px-1 py-2 text-center"
        >
          Không có giá trị
        </div>
      </div>

      <div class="review-col-filter-actions">
        <b-button
          size="sm"
          variant="secondary"
          class="font-weight-bolder"
          @click="clearFilter"
        >
          Xóa lọc
        </b-button>
        <b-button
          size="sm"
          variant="primary"
          class="font-weight-bolder"
          :disabled="!draft.length"
          @click="applyFilter"
        >
          Áp dụng
        </b-button>
      </div>
    </b-dropdown-form>
  </b-dropdown>
</template>

<script>
import { xoa_dau } from '@/utils/common';

export default {
  name: 'TableColumnFilter',
  props: {
    // Nhãn cột (dùng cho tooltip / aria).
    label: {
      type: String,
      default: '',
    },
    // Danh sách giá trị hiển thị DUY NHẤT của cột (cha suy ra từ toàn bộ dữ liệu).
    options: {
      type: Array,
      default: () => [],
    },
    // v-model: mảng giá trị đang chọn (đã commit). Rỗng = không lọc.
    value: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      search: '',
      // Bản nháp lựa chọn trong dropdown, chỉ commit khi bấm "Áp dụng" hoặc thả sau khi kéo.
      draft: [],
      // Mở rộng khung lọc (rộng + cao hơn) để xem nhiều/giá trị dài; giữ trạng thái theo cột.
      expanded: false,
      // Kéo từ dòng này tới dòng kia: chọn đúng dải đó rồi tự áp bộ lọc.
      drag: null,
    };
  },
  computed: {
    // Chuẩn hóa option → { value, label }; giá trị rỗng hiển thị "(Trống)".
    normalizedOptions() {
      return (this.options || []).map((opt) => ({
        value: opt,
        label:
          opt === '' || opt === null || opt === undefined
            ? '(Trống)'
            : String(opt),
      }));
    },
    // Option khớp ô tìm (không dấu, không phân biệt hoa/thường).
    visibleOptions() {
      const keyword = xoa_dau((this.search || '').trim().toLowerCase());
      if (!keyword) return this.normalizedOptions;
      return this.normalizedOptions.filter((opt) =>
        xoa_dau(String(opt.label).toLowerCase()).includes(keyword),
      );
    },
    allVisibleSelected() {
      return (
        this.visibleOptions.length > 0 &&
        this.visibleOptions.every((opt) => this.draft.includes(opt.value))
      );
    },
    someVisibleSelected() {
      return (
        !this.allVisibleSelected &&
        this.visibleOptions.some((opt) => this.draft.includes(opt.value))
      );
    },
    selectAllIconClass() {
      if (this.allVisibleSelected) return 'fas fa-check-square text-primary';
      if (this.someVisibleSelected) return 'fas fa-minus-square text-primary';
      return 'far fa-square text-muted';
    },
    // Cột đang có bộ lọc (đã commit một tập con) → icon đổi màu.
    isActive() {
      return Array.isArray(this.value) && this.value.length > 0;
    },
    toggleClass() {
      return [
        'review-col-filter-toggle',
        { 'review-col-filter-toggle--on': this.isActive },
      ];
    },
  },
  beforeDestroy() {
    this.drag = null;
  },
  methods: {
    onOpen() {
      this.search = '';
      // Không lọc → mặc định tích hết (giống Excel). Đang lọc → khôi phục tập đã chọn.
      this.draft =
        Array.isArray(this.value) && this.value.length
          ? [...this.value]
          : this.normalizedOptions.map((opt) => opt.value);
    },
    isChecked(value) {
      return this.draft.includes(value);
    },
    toggleItem(value) {
      if (this.draft.includes(value)) {
        this.draft = this.draft.filter((item) => item !== value);
      } else {
        this.draft = [...this.draft, value];
      }
    },
    onItemClick(value) {
      if (this.suppressClick) {
        this.suppressClick = false;
        return;
      }
      this.toggleItem(value);
    },
    onItemPointerDown(event) {
      if (event.button !== 0) return;
      const row = event.target.closest('[data-index]');
      const list = this.$refs.list;
      if (!row || !list || !list.contains(row)) return;
      this.drag = {
        anchor: Number(row.dataset.index),
        pointerId: event.pointerId,
        moved: false,
      };
      list.setPointerCapture(event.pointerId);
    },
    onItemPointerMove(event) {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;
      const hit = document.elementFromPoint(event.clientX, event.clientY);
      const row = hit && hit.closest ? hit.closest('[data-index]') : null;
      const list = this.$refs.list;
      if (!row || !list || !list.contains(row)) return;
      const index = Number(row.dataset.index);
      if (index !== this.drag.anchor) this.drag.moved = true;
      if (!this.drag.moved) return;
      this.applyDragRange(index);
    },
    onItemPointerUp(event) {
      if (!this.drag || (event && event.pointerId !== this.drag.pointerId)) return;
      const moved = this.drag.moved;
      this.drag = null;
      if (!moved) return;
      this.suppressClick = true;
      const stopClick = (clickEvent) => {
        clickEvent.preventDefault();
        clickEvent.stopPropagation();
        window.removeEventListener('click', stopClick, true);
      };
      window.addEventListener('click', stopClick, true);
      this.applyFilter();
    },
    applyDragRange(endIndex) {
      const lo = Math.min(this.drag.anchor, endIndex);
      const hi = Math.max(this.drag.anchor, endIndex);
      this.draft = this.visibleOptions
        .slice(lo, hi + 1)
        .map((opt) => opt.value);
    },
    toggleSelectAllRow() {
      // Đang tick hết (theo tập đang lọc-tìm) → bỏ hết; ngược lại → tick hết.
      this.toggleSelectAll(!this.allVisibleSelected);
    },
    toggleSelectAll(checked) {
      const visibleValues = this.visibleOptions.map((opt) => opt.value);
      if (checked) {
        this.draft = Array.from(new Set([...this.draft, ...visibleValues]));
      } else {
        const visibleSet = new Set(visibleValues);
        this.draft = this.draft.filter((value) => !visibleSet.has(value));
      }
    },
    applyFilter() {
      let selected = [...this.draft];
      // Tích hết = không lọc → phát rỗng để cha coi cột này không có bộ lọc.
      if (selected.length >= this.normalizedOptions.length) {
        selected = [];
      }
      this.$emit('input', selected);
      this.close();
    },
    clearFilter() {
      this.$emit('input', []);
      this.draft = this.normalizedOptions.map((opt) => opt.value);
      this.search = '';
      this.close();
    },
    close() {
      if (this.$refs.dd) this.$refs.dd.hide(true);
    },
  },
};
</script>

<style scoped>
::v-deep .review-col-filter-toggle.btn {
  width: 22px;
  min-width: 22px;
  height: 22px;
  min-height: 22px;
  margin: 0;
  padding: 0 !important;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  line-height: 1;
  font-size: 10px;
  font-weight: 400;
  letter-spacing: 0;
  text-transform: none;
  border: 1px solid #c5c9d1 !important;
  border-radius: 3px !important;
  background: linear-gradient(#ffffff, #f3f3f3) !important;
  color: #595959 !important;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.6) !important;
  text-decoration: none !important;
}
::v-deep .review-col-filter-toggle.btn i {
  font-size: 9px !important;
  padding: 0 !important;
  margin: 0 !important;
  line-height: 1 !important;
  display: block;
}
::v-deep .review-col-filter-toggle.btn:hover,
::v-deep .review-col-filter-toggle.btn:focus {
  background: #e1f0ff !important;
  border-color: #3699ff !important;
  color: #3699ff !important;
  text-decoration: none !important;
}
::v-deep .review-col-filter-toggle--on.btn {
  background: #e1f0ff !important;
  border-color: #3699ff !important;
  color: #3699ff !important;
}
::v-deep .review-col-filter-expand.btn {
  width: 30px;
  min-width: 30px;
  height: 30px;
  padding: 0 !important;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
}
::v-deep .review-col-filter-expand.btn i {
  font-size: 12px !important;
  padding: 0 !important;
  margin: 0 !important;
}
::v-deep .review-col-filter-menu {
  width: 420px !important;
  min-width: 420px !important;
  max-width: 420px !important;
  padding: 0;
  border: 1px solid #d0d0d0;
  border-radius: 2px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}
::v-deep .review-col-filter-menu .b-dropdown-form {
  display: block;
  width: 100%;
  margin: 0;
  padding: 8px 10px !important;
}
.review-col-filter-caption {
  margin: 0 0 6px;
  padding-bottom: 6px;
  border-bottom: 1px solid #e6e6e6;
  font-size: 12px;
  font-weight: 600;
  color: #333333;
  line-height: 1.3;
}
.review-col-filter-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  font-size: 9px;
  line-height: 1;
}
.review-col-filter-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid #e6e6e6;
}
::v-deep .review-col-filter-menu--expanded {
  width: 640px !important;
  min-width: 640px !important;
  max-width: 640px !important;
}
.review-col-filter-list {
  max-height: 220px;
  overflow-y: auto;
  user-select: none;
}
.review-col-filter-list.is-dragging {
  cursor: grabbing;
}
.review-col-filter-list.is-dragging .review-col-filter-item {
  cursor: grabbing;
}
.review-col-filter-list--expanded {
  max-height: 420px;
}
.review-col-filter-list--expanded .review-col-filter-label {
  max-width: none;
}
.review-col-filter-item {
  display: flex;
  align-items: center;
  padding: 3px 4px;
  cursor: pointer;
  border-radius: 3px;
  user-select: none;
}
.review-col-filter-item:hover {
  background-color: #f3f6f9;
}
.review-col-filter-item.is-selected {
  background-color: #e1f0ff;
}
.review-col-filter-all {
  padding-bottom: 4px;
  margin-bottom: 4px;
  border-bottom: 1px solid #ebedf3;
}
.review-col-filter-check {
  width: 14px;
  text-align: center;
  flex: 0 0 auto;
}
.review-col-filter-label {
  display: block;
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
